import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronRight, FaServer, FaCloud, FaNetworkWired, FaDatabase } from 'react-icons/fa';
import { useTranslation } from 'react-i18next'; // Ajout de l'import pour les traductions

// Imports des images locales
import slide2 from '../assets/images/security.png';
import slide4 from '../assets/images/sweech.jpg';
import slide3 from '../assets/images/cloud.jpg';
import slide5 from '../assets/images/database.jpg';

// Couleurs exactes de l'AboutSection
const color1 = "#4f837d"; // Vert/bleu foncé
const color2 = "#2e79b9"; // Bleu

const ImageCarousel = () => {
  const { t } = useTranslation(); // Hook pour les traductions
  const [currentSlide, setCurrentSlide] = useState(0);

  // Tableau des slides avec traductions
  const slides = [
    {
      id: 1,
      title: t('home.slides.0.title'),
      description: t('home.slides.0.description'),
      icon: <FaServer className="text-4xl" />,
      color: color1,
      accent: color2,
      image: slide2
    },
    {
      id: 2,
      title: t('home.slides.1.title'),
      description: t('home.slides.1.description'),
      icon: <FaCloud className="text-4xl" />,
      color: color2,
      accent: color1,
      image: slide3
    },
    {
      id: 3,
      title: t('home.slides.2.title'),
      description: t('home.slides.2.description'),
      icon: <FaNetworkWired className="text-4xl" />,
      color: color1,
      accent: color2,
      image: slide4
    },
    {
      id: 4,
      title: t('home.slides.3.title'),
      description: t('home.slides.3.description'),
      icon: <FaDatabase className="text-4xl" />,
      color: color2,
      accent: color1,
      image: slide5
    }
  ];

  // Utilisation de useCallback pour mémoriser la fonction
  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };

  // Auto-avancement des slides avec dépendance corrigée
  useEffect(() => {
    const interval = setInterval(nextSlide, 7000);
    return () => clearInterval(interval);
  }, [nextSlide]); // Ajout de nextSlide comme dépendance

  return (
    <div className="relative w-full min-h-screen overflow-hidden bg-gray-900" id="accueil">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Image d'arrière-plan avec effet de flou */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${slides[currentSlide].image})`,
              filter: 'blur(4px) brightness(0.4)'
            }}
          />
          {/* Overlay dégradé élégant */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-black/90"></div>
          {/* Effets de lumière colorés dynamiques */}
          <div className="absolute inset-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full" style={{ 
              background: `radial-gradient(circle, ${slides[currentSlide].color}20, transparent 70%)`,
              filter: 'blur(100px)'
            }}></div>
            <div className="absolute bottom-1/3 right-1/3 w-80 h-80 rounded-full" style={{ 
              background: `radial-gradient(circle, ${slides[currentSlide].accent}20, transparent 70%)`,
              filter: 'blur(80px)'
            }}></div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 flex flex-col justify-center min-h-screen">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Colonne de texte élégante */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div 
              className="inline-flex items-center gap-3 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                <motion.div 
                  className="text-2xl"
                  style={{ color: slides[currentSlide].color }}
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  {slides[currentSlide].icon}
                </motion.div>
              </div>
              <span className="text-sm font-medium tracking-wider uppercase text-blue-300">
                {t('home.expertise_label')}
              </span>
            </motion.div>

            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <span className="block font-medium mb-3" style={{ 
                color: slides[currentSlide].color,
                textShadow: `0 0 15px ${slides[currentSlide].color}80`
              }}>
                {slides[currentSlide].title}
              </span>
              <span className="block text-xl md:text-2xl font-light text-blue-100 mt-6 max-w-xl mx-auto lg:mx-0">
                {slides[currentSlide].description}
              </span>
            </motion.h1>

            <motion.div
              className="mt-10 flex flex-wrap gap-4 justify-center lg:justify-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <motion.a 
                href="#services" 
                className="px-8 py-4 flex items-center gap-2 rounded-full text-white font-medium transition-all duration-300 shadow-xl overflow-hidden group relative"
                style={{ 
                  background: `linear-gradient(135deg, ${slides[currentSlide].color}, ${slides[currentSlide].accent})`
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <span>{t('home.discover_button')}</span>
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                >
                  <FaChevronRight />
                </motion.span>
              </motion.a>
              <a 
                href="#contact"
                className="px-8 py-4 flex items-center gap-2 rounded-full font-medium transition-all duration-300 border border-white/30 text-white hover:bg-white/10 hover:border-white/50 relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 rounded-full"></div>
                <span>{t('home.contact_button')}</span>
              </a>
            </motion.div>
          </motion.div>

          {/* Colonne visuelle */}
          <motion.div 
            className="relative h-[500px] flex items-center justify-center"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Cadre élégant pour l'image */}
            <div className="relative w-full max-w-[90%] h-[80%]">
              {/* Effet de lumière */}
              <div 
                className="absolute -inset-4 rounded-2xl opacity-70"
                style={{
                  background: `conic-gradient(
                    from 180deg at 50% 50%,
                    ${color1}30,
                    ${color2}30,
                    ${color1}30
                  )`,
                  filter: 'blur(15px)'
                }}
              ></div>
              
              {/* Effet de cadre flottant */}
              <motion.div
                className="absolute -inset-4 bg-gradient-to-br from-transparent via-white/10 to-transparent rounded-2xl transform"
                animate={{ 
                  rotate: [0, 1, -1, 0],
                }}
                transition={{ 
                  duration: 8, 
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              ></motion.div>
              
              {/* Image principale */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  className="absolute inset-0 rounded-xl overflow-hidden shadow-2xl border-4 border-white/20"
                  style={{
                    boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px -10px ${slides[currentSlide].color}80`
                  }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.8 }}
                >
                  <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  
                  {/* Effet de reflet */}
                  <div 
                    className="absolute bottom-0 left-0 right-0 h-16"
                    style={{
                      background: `linear-gradient(to top, ${slides[currentSlide].color}30, transparent)`,
                      backdropFilter: 'blur(2px)'
                    }}
                  ></div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Indicateurs de slide */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? `scale-125 ${slides[currentSlide].color === color1 ? 'bg-[#4f837d]' : 'bg-[#2e79b9]'}` 
                      : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
            
            {/* Flèches de navigation */}
            <motion.button
              onClick={prevSlide}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 flex items-center justify-center rounded-full z-20 group"
              style={{ 
                background: `linear-gradient(135deg, ${color1}30, ${color2}30)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${color1}50`
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={t('aria.previous_slide')}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-8 w-8 text-white group-hover:text-[#4f837d] transition-colors"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </motion.button>
            
            <motion.button
              onClick={nextSlide}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 flex items-center justify-center rounded-full z-20 group"
              style={{ 
                background: `linear-gradient(135deg, ${color1}30, ${color2}30)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${color1}50`
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={t('aria.next_slide')}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-8 w-8 text-white group-hover:text-[#4f837d] transition-colors"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.button>
          </motion.div>
        </div>
      </div>
      
      {/* Barre de progression animée */}
      <motion.div 
        className="absolute bottom-0 left-0 h-1 z-10"
        style={{ 
          background: `linear-gradient(to right, ${color1}, ${color2})`,
          boxShadow: `0 0 15px ${color2}`
        }}
        initial={{ width: 0 }}
        animate={{ width: "100%" }}
        transition={{ duration: 7, ease: "linear" }}
        key={currentSlide}
      />
      
      {/* Effet de particules */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              background: `radial-gradient(circle, ${color2}30, transparent 70%)`,
              width: `${Math.random() * 20 + 5}px`,
              height: `${Math.random() * 20 + 5}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              x: [0, (Math.random() - 0.5) * 20, 0],
              opacity: [0.3, 0.7, 0.3],
              scale: [1, 1.2, 1]
            }}
            transition={{
              duration: Math.random() * 10 + 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;